
import { Product, CertificationStatus } from "@/types/portfolio";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Clock, CheckCircle, AlertTriangle, Package, Edit, Leaf, Users } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { usePersonaContext } from "@/contexts/PersonaContext";

interface ProductCardProps {
  product: Product;
  onDelete: (id: string) => void;
  onEdit: (product: Product) => void;
}

export const ProductCard = ({ product, onDelete, onEdit }: ProductCardProps) => {
  const navigate = useNavigate();
  const { portfolioId } = useParams();
  const { currentPersona } = usePersonaContext();

  const getCertificationIcon = (status: CertificationStatus) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 mr-1" />;
      case "in_progress":
        return <Clock className="h-4 w-4 mr-1 text-blue-500" />;
      case "certified":
        return <CheckCircle className="h-4 w-4 mr-1 text-green-500" />;
      case "rejected":
        return <AlertTriangle className="h-4 w-4 mr-1 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 mr-1" />;
    }
  };

  const getCertificationColor = (status: CertificationStatus) => {
    switch (status) {
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "certified":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleCardClick = () => {
    const gsPersonaNames = ['GS Cert Platform - Client', 'GS Document Tool User'];
    const basePath = gsPersonaNames.includes(currentPersona?.name || '') ? '/gs-cert-client' : '/client';
    navigate(`${basePath}/portfolios/${portfolioId}/products/${product.id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(product);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(product.id);
  };

  return (
    <Card className="h-full flex flex-col cursor-pointer hover:shadow-lg transition-shadow" onClick={handleCardClick}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">{product.name}</CardTitle>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCertificationColor(
              product.certification_status
            )}`}
          >
            {getCertificationIcon(product.certification_status)}
            {product.certification_status.replace("_", " ")}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-sm text-muted-foreground mb-2">
          {product.description || "No description provided."}
        </p>
        {product.manufacturer && (
          <p className="text-sm text-muted-foreground mb-2">
            <span className="font-medium">Manufacturer:</span> {product.manufacturer}
          </p>
        )}
        
        {/* Safety and certification indicators - only show for certified products */}
        {product.certification_status === "certified" && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center text-green-600" title="Safe for humans">
                <Users className="h-4 w-4 mr-1" />
                <span className="text-xs">Human safe</span>
              </div>
              <div className="flex items-center text-green-600" title="Safe for the environment">
                <Leaf className="h-4 w-4 mr-1" />
                <span className="text-xs">Eco-friendly</span>
              </div>
            </div>
            
            <div title="Green Seal Certified">
              <img 
                src="/lovable-uploads/db5c9f04-b178-4abc-b8ed-9e09d1e7c292.png" 
                alt="Green Seal Certified" 
                className="h-8 w-auto" 
              />
            </div>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground mt-4">
          Added {formatDistanceToNow(new Date(product.created_at), { addSuffix: true })}
        </p>
      </CardContent>
      <CardFooter className="pt-2 border-t flex justify-end gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={handleEditClick}
          title="Edit Product"
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="text-destructive"
          onClick={handleDeleteClick}
          title="Delete Product"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};
